import React, { useState } from 'react';
import SelectionScreen from './SelectionScreen';
import NumberNamingQuestionScreen from './NumberNamingQuestionScreen';
import SubitizingQuestionScreen from './SubitizingQuestionScreen';
import CountingQuestionScreen from './CountingQuestionScreen';
import WhatComesNextQuestionScreen from './WhatComesNextQuestionScreen';
import PreKResultScreen from './PreKResultScreen';
import { Screen, PreKSettings, DiagnosticAssessmentResult } from './types';
import { useAuth } from '../../context/AuthContext';
import UserProfileDropdown from '../Dashboard/UserProfileDropdown';

// Convert assessment type to track ID for JourneyPath
const assessmentTypeToTrackId = (assessmentType: 'numbernaming' | 'subitizing' | 'counting' | 'whatcomesnext'): string => {
  switch (assessmentType) {
    case 'subitizing': return 'TRACK_PREK_SUBITIZING';
    case 'counting': return 'TRACK_PREK_COUNTING';
    case 'whatcomesnext': return 'TRACK_PREK_WHATCOMESNEXT';
    default: return 'TRACK_PREK_NAMING';
  }
};

// Convert focusTrack to assessment type for initial state
const focusTrackToAssessmentType = (focusTrack?: string): 'numbernaming' | 'subitizing' | 'counting' | 'whatcomesnext' => {
  switch (focusTrack) {
    case 'TRACK_PREK_SUBITIZING': return 'subitizing';
    case 'TRACK_PREK_COUNTING': return 'counting';
    case 'TRACK_PREK_WHATCOMESNEXT': return 'whatcomesnext';
    default: return 'numbernaming';
  }
};

const PreKAssessment: React.FC = () => {
  const { user } = useAuth();
  const [currentScreen, setCurrentScreen] = useState<Screen>('selection');
  const [settings, setSettings] = useState<PreKSettings | null>(null);
  const [results, setResults] = useState<DiagnosticAssessmentResult | null>(null);
  const [currentAssessmentType, setCurrentAssessmentType] = useState<'numbernaming' | 'subitizing' | 'counting' | 'whatcomesnext'>(focusTrackToAssessmentType(user?.focusTrack));

  const handleStart = (newSettings: PreKSettings) => {
    setSettings(newSettings);

    // Route to appropriate assessment based on user's focus track
    if (user?.focusTrack === 'TRACK_PREK_SUBITIZING') {
      setCurrentScreen('subitizing');
      setCurrentAssessmentType('subitizing');
    } else if (user?.focusTrack === 'TRACK_PREK_COUNTING') {
      setCurrentScreen('counting');
      setCurrentAssessmentType('counting');
    } else if (user?.focusTrack === 'TRACK_PREK_WHATCOMESNEXT') {
      setCurrentScreen('whatcomesnext');
      setCurrentAssessmentType('whatcomesnext');
    } else {
      // For TRACK_PREK_NAMING or no focus track, start with number naming (original sequence)
      setCurrentScreen('question');
      setCurrentAssessmentType('numbernaming');
    }
  };

  const handleNumberNamingComplete = (assessmentResults: DiagnosticAssessmentResult) => {
    setResults(assessmentResults);
    setCurrentAssessmentType('numbernaming');
    setCurrentScreen('result');
  };

  const handleSubitizingComplete = (assessmentResults: DiagnosticAssessmentResult) => {
    setResults(assessmentResults);
    setCurrentAssessmentType('subitizing');
    setCurrentScreen('result');
  };

  const handleCountingComplete = (assessmentResults: DiagnosticAssessmentResult) => {
    setResults(assessmentResults);
    setCurrentAssessmentType('counting');
    setCurrentScreen('result');
  };

  const handleWhatComesNextComplete = (assessmentResults: DiagnosticAssessmentResult) => {
    setResults(assessmentResults);
    setCurrentAssessmentType('whatcomesnext');
    setCurrentScreen('result');
  };

  const handleRestart = () => {
    setSettings(null);
    setResults(null);
    setCurrentScreen('selection');
    setCurrentAssessmentType('numbernaming');
  };

  const handleContinueFromResult = () => {
    if (currentAssessmentType === 'numbernaming') {
      // After number naming, go to subitizing
      setCurrentScreen('subitizing');
    } else if (currentAssessmentType === 'subitizing') {
      // After subitizing, go to counting
      setCurrentScreen('counting');
    } else if (currentAssessmentType === 'counting') {
      // After counting, go to what comes next
      setCurrentScreen('whatcomesnext');
    } else {
      // After what comes next, we're done - this case shouldn't be reached since
      // PreKResultScreen now handles navigation directly to dashboard
      setCurrentScreen('selection');
    }
  };

  return (
    <div className="relative">
      {user?.role === 'admin' && (
        <div className="absolute top-4 right-4 z-50">
          <UserProfileDropdown />
        </div>
      )}
      {currentScreen === 'selection' && (
        <SelectionScreen onStart={handleStart} currentTrack={assessmentTypeToTrackId(currentAssessmentType)} />
      )}
      {currentScreen === 'question' && settings && (
        <NumberNamingQuestionScreen
          settings={settings}
          onComplete={handleNumberNamingComplete}
        />
      )}
      {currentScreen === 'subitizing' && settings && (
        <SubitizingQuestionScreen
          settings={settings}
          onComplete={handleSubitizingComplete}
        />
      )}
      {currentScreen === 'counting' && settings && (
        <CountingQuestionScreen
          settings={settings}
          onComplete={handleCountingComplete}
        />
      )}
      {currentScreen === 'whatcomesnext' && settings && (
        <WhatComesNextQuestionScreen
          settings={settings}
          onComplete={handleWhatComesNextComplete}
        />
      )}
      {currentScreen === 'result' && results && (
        <PreKResultScreen
          results={results}
          assessmentType={currentAssessmentType}
          onContinue={handleContinueFromResult}
        />
      )}
    </div>
  );
};

export default PreKAssessment;